import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { CreateGroupModal, EditGroupModal } from '../components/GroupModals';
import { EVERYONE_GROUP } from '../constants';

export default function GroupsPage() {
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [friendsCount, setFriendsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editGroup, setEditGroup] = useState(null);

  const load = useCallback(async () => {
    try {
      const [g, f] = await Promise.all([api.getGroups(), api.getFriends().catch(() => [])]);
      setGroups(g);
      setFriendsCount(Array.isArray(f) ? f.length : 0);
    }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleLeave = async (g) => {
    if (!window.confirm(`Leave "${g.name}"?`)) return;
    try { await api.leaveGroup(g.id); load(); } catch (err) { alert(err.message); }
  };

  if (loading) return <div className="empty-state"><p>Loading…</p></div>;

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginBottom:'1.1rem', flexWrap:'wrap', gap:'.75rem' }}>
        <div>
          <h2 style={{ fontSize: 22 }}>My groups</h2>
          <p style={{ fontSize: 12, color: '#7a7a6a', marginTop: 1 }}>Share tee times privately with your crew</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>+ New group</button>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(230px, 1fr))', gap:'0.9rem' }}>
        {/* Everyone — pinned at top, can't be edited or left */}
        <div style={{ background:'#fff', borderRadius:12, border:'1.5px solid #c8e8d0', overflow:'hidden' }}>
          <div style={{ padding:'0.9rem 1rem 0.75rem', borderBottom:'0.5px solid rgba(0,0,0,.06)' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
              <div style={{ width:30, height:30, borderRadius:7, background:EVERYONE_GROUP.color, display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, marginBottom:6 }}>
                {EVERYONE_GROUP.emoji}
              </div>
              <span style={{ fontSize:10, color:'#1a5c32', background:'#e8f5ec', padding:'2px 7px', borderRadius:8, fontWeight:500 }}>Auto</span>
            </div>
            <div style={{ fontWeight:500, fontSize:14, color:'#0d2b1a' }}>{EVERYONE_GROUP.name}</div>
            <div style={{ fontSize:12, color:'#7a7a6a', marginTop:1 }}>{EVERYONE_GROUP.description}</div>
          </div>
          <div style={{ padding:'0.65rem 1rem' }}>
            <span style={{ fontSize:12, color:'#5a5a4a' }}>{friendsCount} friend{friendsCount === 1 ? '' : 's'}</span>
          </div>
        </div>

        {groups.map(g => {
          const isOwner = String(g.owner_id) === String(user.id);
          return (
            <div key={g.id} style={{ background:'#fff', borderRadius:12, border:'0.5px solid rgba(0,0,0,.08)', overflow:'hidden' }}>
              <div style={{ padding:'0.9rem 1rem 0.75rem', borderBottom:'0.5px solid rgba(0,0,0,.06)' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                  <div style={{ width:30, height:30, borderRadius:7, background:g.color, display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, marginBottom:6 }}>
                    {g.emoji}
                  </div>
                  {isOwner && (
                    <button className="btn btn-ghost btn-xs" onClick={() => setEditGroup(g)} style={{ fontSize:11 }}>Edit</button>
                  )}
                </div>
                <div style={{ fontWeight:500, fontSize:14, color:'#0d2b1a' }}>{g.name}</div>
                <div style={{ fontSize:12, color:'#7a7a6a', marginTop:1 }}>{g.description || 'No description'}</div>
              </div>
              <div style={{ padding:'0.65rem 1rem', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <span style={{ fontSize:12, color:'#5a5a4a' }}>{g.member_count} members · {g.tee_time_count} tee times</span>
                <div style={{ display:'flex', gap:5 }}>
                  {!isOwner && <button className="btn btn-xs btn-outline" onClick={() => handleLeave(g)}>Leave</button>}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {showCreate && <CreateGroupModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
      {editGroup && (
        <EditGroupModal
          group={editGroup}
          onClose={() => setEditGroup(null)}
          onSaved={() => { setEditGroup(null); load(); }}
          onDeleted={() => { setEditGroup(null); load(); }}
        />
      )}
    </div>
  );
}
